import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Settings, Plus, Edit, Trash2 } from "lucide-react";
import { useEquipamentos, useCriarEquipamento, useEditarEquipamento, useExcluirEquipamento } from "@/hooks/useEquipamentos";
import { toast } from "sonner";

export function Equipamentos() {
  const [tipoSelecionado, setTipoSelecionado] = useState("painel");
  const [novoEquipamento, setNovoEquipamento] = useState({
    tipo: "painel",
    marca: "",
    modelo: "",
    potencia: "",
    preco_unitario: "",
    especificacoes: ""
  });

  const [editarEquipamento, setEditarEquipamento] = useState<{
    id: number;
    tipo: string;
    marca: string;
    modelo: string;
    potencia: string;
    preco_unitario: string;
    especificacoes: string;
  } | null>(null);

  const [dialogEditar, setDialogEditar] = useState(false);
  const [errors, setErrors] = useState<{[key: string]: string}>({});

  const { data: equipamentos, isLoading } = useEquipamentos();
  const criarEquipamento = useCriarEquipamento();
  const editarEquipamentoMutation = useEditarEquipamento();
  const excluirEquipamento = useExcluirEquipamento();

  const equipamentosFiltrados = equipamentos?.filter(eq => eq.tipo === tipoSelecionado) || [];

  const validateForm = (data: any) => {
    const newErrors: {[key: string]: string} = {};
    
    if (!data.marca.trim()) {
      newErrors.marca = "Marca é obrigatória";
    }
    
    if (!data.modelo.trim()) {
      newErrors.modelo = "Modelo é obrigatório";
    }
    
    if (data.preco_unitario && parseFloat(data.preco_unitario) < 0) {
      newErrors.preco_unitario = "Preço deve ser maior que zero";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleAdicionarEquipamento = async () => {
    if (!validateForm(novoEquipamento)) {
      toast.error("Por favor, corrija os erros no formulário");
      return;
    }

    try {
      let especificacoes = {};
      if (novoEquipamento.especificacoes.trim()) {
        try {
          especificacoes = JSON.parse(novoEquipamento.especificacoes);
        } catch {
          // Se não for JSON válido, salvar como string
          especificacoes = { observacoes: novoEquipamento.especificacoes };
        }
      }

      const dadosParaEnviar = {
        tipo: novoEquipamento.tipo,
        marca: novoEquipamento.marca.trim(),
        modelo: novoEquipamento.modelo.trim(),
        potencia: novoEquipamento.potencia.trim() || undefined,
        preco_unitario: novoEquipamento.preco_unitario ? parseFloat(novoEquipamento.preco_unitario) : undefined,
        especificacoes
      };

      await criarEquipamento.mutateAsync(dadosParaEnviar);

      toast.success("Equipamento adicionado com sucesso!");
      
      // Limpar form
      setNovoEquipamento({
        tipo: tipoSelecionado,
        marca: "",
        modelo: "",
        potencia: "",
        preco_unitario: "",
        especificacoes: ""
      });
      setErrors({});
    } catch (error) {
      console.error('Erro ao adicionar equipamento:', error);
      toast.error("Erro ao adicionar equipamento. Verifique os dados e tente novamente.");
    }
  };

  const handleEditarEquipamento = async () => {
    if (!editarEquipamento || !validateForm(editarEquipamento)) {
      toast.error("Por favor, corrija os erros no formulário");
      return;
    }

    try {
      let especificacoes = {};
      if (editarEquipamento.especificacoes.trim()) {
        try {
          especificacoes = JSON.parse(editarEquipamento.especificacoes);
        } catch {
          // Se não for JSON válido, salvar como string
          especificacoes = { observacoes: editarEquipamento.especificacoes };
        }
      }

      const dadosParaEnviar = {
        id: editarEquipamento.id,
        tipo: editarEquipamento.tipo,
        marca: editarEquipamento.marca.trim(),
        modelo: editarEquipamento.modelo.trim(),
        potencia: editarEquipamento.potencia.trim() || undefined,
        preco_unitario: editarEquipamento.preco_unitario ? parseFloat(editarEquipamento.preco_unitario) : undefined,
        especificacoes
      };

      await editarEquipamentoMutation.mutateAsync(dadosParaEnviar);

      toast.success("Equipamento editado com sucesso!");
      setDialogEditar(false);
      setEditarEquipamento(null);
      setErrors({});
    } catch (error) {
      console.error('Erro ao editar equipamento:', error);
      toast.error("Erro ao editar equipamento. Verifique os dados e tente novamente.");
    }
  };

  const handleExcluirEquipamento = async (id: number) => {
    try {
      await excluirEquipamento.mutateAsync(id);
      toast.success("Equipamento excluído com sucesso!");
    } catch (error) {
      console.error('Erro ao excluir equipamento:', error);
      toast.error("Erro ao excluir equipamento.");
    }
  };

  const abrirDialogEditar = (equipamento: any) => {
    setEditarEquipamento({
      id: equipamento.id,
      tipo: equipamento.tipo,
      marca: equipamento.marca,
      modelo: equipamento.modelo,
      potencia: equipamento.potencia || "",
      preco_unitario: equipamento.preco_unitario ? equipamento.preco_unitario.toString() : "",
      especificacoes: equipamento.especificacoes ? 
        (typeof equipamento.especificacoes === 'object' ? 
          JSON.stringify(equipamento.especificacoes) : 
          equipamento.especificacoes
        ) : ""
    });
    setDialogEditar(true);
    setErrors({});
  };

  const getTipoNome = (tipo: string) => {
    switch (tipo) {
      case 'painel': return 'Painéis Solares';
      case 'inversor': return 'Inversores';
      case 'estrutura': return 'Estruturas';
      case 'outros': return 'Outros Produtos';
      default: return tipo;
    }
  };

  const getTipoNomeSingular = (tipo: string) => {
    switch (tipo) {
      case 'painel': return 'painel';
      case 'inversor': return 'inversor';
      case 'estrutura': return 'estrutura';
      case 'outros': return 'produto';
      default: return tipo;
    }
  };

  const handleTipoChange = (novoTipo: string) => {
    setTipoSelecionado(novoTipo);
    setNovoEquipamento(prev => ({ ...prev, tipo: novoTipo }));
    setErrors({});
  };

  const handleInputChange = (field: string, value: string) => {
    setNovoEquipamento(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: "" }));
    }
  };

  const handleEditarInputChange = (field: string, value: string) => {
    if (editarEquipamento) {
      setEditarEquipamento(prev => ({ ...prev!, [field]: value }));
      if (errors[field]) {
        setErrors(prev => ({ ...prev, [field]: "" }));
      }
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-3xl font-bold text-gray-900 mb-2">Equipamentos</h2>
        <p className="text-gray-600">Gerencie seu catálogo de equipamentos para orçamentos e projetos</p>
      </div>

      <Tabs value={tipoSelecionado} onValueChange={handleTipoChange} className="w-full">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="painel">Painéis</TabsTrigger>
          <TabsTrigger value="inversor">Inversores</TabsTrigger>
          <TabsTrigger value="estrutura">Estruturas</TabsTrigger>
          <TabsTrigger value="outros">Outros</TabsTrigger>
        </TabsList>

        <TabsContent value={tipoSelecionado} className="space-y-6">
          {/* Formulário para adicionar equipamento */}
          <Card className="border-0 shadow-md">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-gray-900">
                <Plus className="h-5 w-5 text-green-600" />
                Adicionar {getTipoNome(tipoSelecionado)}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <div>
                  <Label>Tipo</Label>
                  <Select 
                    value={novoEquipamento.tipo} 
                    onValueChange={(value) => handleInputChange('tipo', value)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="painel">Painel Solar</SelectItem>
                      <SelectItem value="inversor">Inversor</SelectItem>
                      <SelectItem value="estrutura">Estrutura</SelectItem>
                      <SelectItem value="outros">Outros</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Marca *</Label>
                  <Input
                    placeholder="Ex: Canadian Solar"
                    value={novoEquipamento.marca}
                    onChange={(e) => handleInputChange('marca', e.target.value)}
                    className={errors.marca ? "border-red-500" : ""}
                  />
                  {errors.marca && (
                    <p className="text-xs text-red-500 mt-1">{errors.marca}</p>
                  )}
                </div>

                <div>
                  <Label>Modelo *</Label>
                  <Input
                    placeholder="Ex: CS3K-300P"
                    value={novoEquipamento.modelo}
                    onChange={(e) => handleInputChange('modelo', e.target.value)}
                    className={errors.modelo ? "border-red-500" : ""}
                  />
                  {errors.modelo && (
                    <p className="text-xs text-red-500 mt-1">{errors.modelo}</p>
                  )}
                </div>

                <div>
                  <Label>Potência</Label>
                  <Input
                    placeholder="Ex: 300W"
                    value={novoEquipamento.potencia}
                    onChange={(e) => handleInputChange('potencia', e.target.value)}
                  />
                </div>

                <div>
                  <Label>Preço Unitário (R$)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="Ex: 450.00"
                    value={novoEquipamento.preco_unitario}
                    onChange={(e) => handleInputChange('preco_unitario', e.target.value)}
                    className={errors.preco_unitario ? "border-red-500" : ""}
                  />
                  {errors.preco_unitario && (
                    <p className="text-xs text-red-500 mt-1">{errors.preco_unitario}</p>
                  )}
                </div>

                <div className="md:col-span-1">
                  <Label>Especificações (JSON ou texto)</Label>
                  <Textarea
                    placeholder='Ex: {"eficiencia": "15.88%"} ou texto livre'
                    value={novoEquipamento.especificacoes}
                    onChange={(e) => handleInputChange('especificacoes', e.target.value)}
                    className="h-20"
                    spellCheck="false"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Digite em formato JSON ou texto livre para observações
                  </p>
                </div>
              </div>

              <Button 
                onClick={handleAdicionarEquipamento}
                disabled={criarEquipamento.isPending || !novoEquipamento.marca.trim() || !novoEquipamento.modelo.trim()}
                className="bg-primary hover:bg-primary/90 disabled:opacity-50"
              >
                {criarEquipamento.isPending ? "Adicionando..." : "Adicionar Equipamento"}
              </Button>
            </CardContent>
          </Card>

          {/* Lista de equipamentos */}
          <Card className="border-0 shadow-md">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-gray-900">
                <Settings className="h-5 w-5 text-green-600" />
                {getTipoNome(tipoSelecionado)} Cadastrados
              </CardTitle>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600 mx-auto mb-4"></div>
                  <p>Carregando equipamentos...</p>
                </div>
              ) : equipamentosFiltrados.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Settings className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p>Nenhum {getTipoNomeSingular(tipoSelecionado)} cadastrado</p>
                  <p className="text-sm">Adicione equipamentos usando o formulário acima</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead align="center">Marca</TableHead>
                        <TableHead align="center">Modelo</TableHead>
                        {tipoSelecionado !== 'estrutura' && <TableHead align="center">Potência</TableHead>}
                        <TableHead align="center">Preço Unit.</TableHead>
                        <TableHead align="center">Especificações</TableHead>
                        <TableHead align="center">Ações</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {equipamentosFiltrados.map((equipamento) => (
                        <TableRow key={equipamento.id}>
                          <TableCell align="left" className="font-medium">{equipamento.marca}</TableCell>
                          <TableCell align="left">{equipamento.modelo}</TableCell>
                          {tipoSelecionado !== 'estrutura' && (
                            <TableCell align="center">{equipamento.potencia || '-'}</TableCell>
                          )}
                          <TableCell align="center">
                            {equipamento.preco_unitario ? 
                              `R$ ${equipamento.preco_unitario.toFixed(2)}` : 
                              '-'
                            }
                          </TableCell>
                          <TableCell align="left">
                            <div className="max-w-xs truncate">
                              {equipamento.especificacoes ? 
                                (typeof equipamento.especificacoes === 'object' ? 
                                  JSON.stringify(equipamento.especificacoes) : 
                                  equipamento.especificacoes
                                ) : 
                                '-'
                              }
                            </div>
                          </TableCell>
                          <TableCell align="center">
                            <div className="flex gap-1 justify-center">
                              <Button 
                                variant="outline" 
                                size="sm" 
                                title="Editar"
                                onClick={() => abrirDialogEditar(equipamento)}
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <Button variant="outline" size="sm" title="Excluir">
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>Confirmar exclusão</AlertDialogTitle>
                                    <AlertDialogDescription>
                                      Tem certeza que deseja excluir o equipamento "{equipamento.marca} {equipamento.modelo}"?
                                      Esta ação não pode ser desfeita.
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                    <AlertDialogAction
                                      onClick={() => handleExcluirEquipamento(equipamento.id)}
                                      className="bg-red-600 hover:bg-red-700"
                                    >
                                      Excluir
                                    </AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Modal de Edição */}
      <Dialog open={dialogEditar} onOpenChange={setDialogEditar}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Editar Equipamento</DialogTitle>
          </DialogHeader>
          {editarEquipamento && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Tipo</Label>
                  <Select 
                    value={editarEquipamento.tipo} 
                    onValueChange={(value) => handleEditarInputChange('tipo', value)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="painel">Painel Solar</SelectItem>
                      <SelectItem value="inversor">Inversor</SelectItem>
                      <SelectItem value="estrutura">Estrutura</SelectItem>
                      <SelectItem value="outros">Outros</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Marca *</Label>
                  <Input
                    placeholder="Ex: Canadian Solar"
                    value={editarEquipamento.marca}
                    onChange={(e) => handleEditarInputChange('marca', e.target.value)}
                    className={errors.marca ? "border-red-500" : ""}
                  />
                  {errors.marca && (
                    <p className="text-xs text-red-500 mt-1">{errors.marca}</p>
                  )}
                </div>

                <div>
                  <Label>Modelo *</Label>
                  <Input
                    placeholder="Ex: CS3K-300P"
                    value={editarEquipamento.modelo}
                    onChange={(e) => handleEditarInputChange('modelo', e.target.value)}
                    className={errors.modelo ? "border-red-500" : ""}
                  />
                  {errors.modelo && (
                    <p className="text-xs text-red-500 mt-1">{errors.modelo}</p>
                  )}
                </div>

                <div>
                  <Label>Potência</Label>
                  <Input
                    placeholder="Ex: 300W"
                    value={editarEquipamento.potencia}
                    onChange={(e) => handleEditarInputChange('potencia', e.target.value)}
                  />
                </div>

                <div>
                  <Label>Preço Unitário (R$)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="Ex: 450.00"
                    value={editarEquipamento.preco_unitario}
                    onChange={(e) => handleEditarInputChange('preco_unitario', e.target.value)}
                    className={errors.preco_unitario ? "border-red-500" : ""}
                  />
                  {errors.preco_unitario && (
                    <p className="text-xs text-red-500 mt-1">{errors.preco_unitario}</p>
                  )}
                </div>

                <div className="md:col-span-2">
                  <Label>Especificações (JSON ou texto)</Label>
                  <Textarea
                    placeholder='Ex: {"eficiencia": "15.88%"} ou texto livre'
                    value={editarEquipamento.especificacoes}
                    onChange={(e) => handleEditarInputChange('especificacoes', e.target.value)}
                    className="h-20"
                    spellCheck="false"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Digite em formato JSON ou texto livre para observações
                  </p>
                </div>
              </div>

              <div className="flex justify-end gap-2">
                <Button 
                  variant="outline" 
                  onClick={() => setDialogEditar(false)}
                >
                  Cancelar
                </Button>
                <Button 
                  onClick={handleEditarEquipamento}
                  disabled={editarEquipamentoMutation.isPending || !editarEquipamento.marca.trim() || !editarEquipamento.modelo.trim()}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  {editarEquipamentoMutation.isPending ? "Salvando..." : "Salvar Alterações"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}